// #97 AckPair (interview V-3, DC-23): an interviewer's answer to a seat inside a table cell — *Accept* (the one red
// primary) beside *Decline* (ghost), both small. Once answered, or for a seat that only views, the cell shows the
// acknowledgment status pill instead (Acknowledgment Pending · Accepted · Declined). A *nonresponse* (the seat went
// unanswered past the threshold, DS row 99) is drawn like a pending seat while it can still be answered (the pair),
// else its own word on the warn pill the Responses section gives it (DS §1.4: one tone per meaning). `busy` names the button whose
// request is in flight: it keeps its label under aria-busy and the other is inert too, and says so (aria-disabled).
// When the seat is answered from the pair, focus moves to the status pill that replaces the buttons (the pill is a
// programmatic focus target only then; a view-only pill is not focusable).

import { useRef } from "react";
import { cx } from "../lib/cx";
import { useIsoLayoutEffect } from "../lib/useIsoLayoutEffect";
import { ACKNOWLEDGMENT_STATUS, ACTION_LABELS, RESPONSE_STATUS } from "../labels";
import { Button, type Blocked } from "../core/Button";
import { Pill } from "../core/Pill";
import type { Hs } from "../lib/hs";

export type AckStatus = "pending" | "nonresponse" | "accepted" | "declined";

/** A seat still open to an answer. */
const open = (status: AckStatus) => status === "pending" || status === "nonresponse";

const STATUS_WORD: Readonly<Record<AckStatus, string>> = {
  pending: ACKNOWLEDGMENT_STATUS.acknowledgmentPending,
  nonresponse: RESPONSE_STATUS.nonresponse,
  accepted: ACKNOWLEDGMENT_STATUS.accepted,
  declined: ACKNOWLEDGMENT_STATUS.declined,
};

export interface AckPairProps {
  status?: AckStatus | undefined;
  onAccept?: (() => void) | undefined;
  onDecline?: (() => void) | undefined;
  /** The request in flight. */
  busy?: "accept" | "decline" | undefined;
  /** Ties both buttons to the row they answer (the round, the candidate), for assistive technology. */
  describedBy?: string | undefined;
  can?: boolean | undefined;
  blocked?: Blocked | undefined;
  mode?: "edit" | "view" | undefined;
  hs?: { accept?: Hs | undefined; decline?: Hs | undefined } | undefined;
  className?: string | undefined;
}

export function AckPair({ status = "pending", onAccept, onDecline, busy, describedBy, can, blocked, mode, hs, className }: AckPairProps) {
  const answered = useRef<HTMLSpanElement>(null);
  const was = useRef(status);
  useIsoLayoutEffect(() => {
    const before = was.current;
    was.current = status;
    // The buttons that held the focus are gone; the answer they gave takes it, unless the focus is elsewhere already.
    const lost = document.activeElement === null || document.activeElement === document.body;
    if (open(before) && !open(status) && lost) answered.current?.focus();
  }, [status]);
  if (can === false) return null;
  if (mode === "view" || !open(status)) {
    return <span ref={answered} tabIndex={mode === "view" ? undefined : -1} className={cx("mc-ack", className)}><Pill tone={status === "nonresponse" ? "warn" : status === "pending" ? "neutral" : undefined}>{STATUS_WORD[status]}</Pill></span>;
  }
  return (
    <span className={cx("mc-ack", className)}>
      <Button sm tone="primary" busy={busy === "accept"} blocked={blocked} aria-describedby={describedBy}
        aria-disabled={busy === "decline" ? true : undefined}
        onClick={busy ? undefined : onAccept} hs={hs?.accept}>
        {ACTION_LABELS.accept}
      </Button>
      <Button sm tone="ghost" busy={busy === "decline"} blocked={blocked} aria-describedby={describedBy}
        aria-disabled={busy === "accept" ? true : undefined}
        onClick={busy ? undefined : onDecline} hs={hs?.decline}>
        {ACTION_LABELS.decline}
      </Button>
    </span>
  );
}
